import { useAtomValue } from "jotai";
import React from "react";
import { ReactElement, ReactNode } from "react";
import { ExternalLink, ChevronRight, ChevronDown } from "lucide-react";
import { SidebarStateAtom } from "../../../store";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../components/ui/tooltip";

interface NavItemProps {
  label: string;
  subtle?: boolean;
  active?: boolean;
  icon: ReactElement;
  endElement?: ReactElement;
  children?: ReactNode;
  onClick?: () => void;
  className?: string;
  isExternal?: boolean;
  isCollapsed?: boolean;
  hasChildren?: boolean;
  isExpanded?: boolean;
  disabled?: boolean;
}

export const NavItem = (props: NavItemProps) => {
  const {
    active,
    subtle,
    icon,
    children,
    label,
    endElement,
    onClick,
    className,
    isExternal,
    isCollapsed = false,
    hasChildren = false,
    isExpanded = false,
    disabled = false,
  } = props;

  const item = (
    <div
      role="button"
      className={cn(
        "flex items-center w-full px-3 py-1.5",
        "cursor-pointer select-none rounded-md",
        "transition-colors duration-200",
        !disabled && "hover:bg-muted",
        active
          ? "bg-muted/50 text-primary font-medium"
          : "text-muted-foreground",
        disabled && "opacity-50 cursor-not-allowed",
        isCollapsed ? "justify-center" : "justify-start",
        !isCollapsed &&
          !subtle &&
          "relative before:content-[''] before:absolute before:w-3 before:h-4 before:border-b before:border-l before:border-gray-400 before:rounded-bl-md before:top-[calc(50%-15px)] before:-left-3 transform translate-x-[-1px]",
        className
      )}
      onClick={disabled ? undefined : onClick}
    >
      <div
        className={cn(
          "text-xs",
          active ? "text-primary" : "text-muted-foreground",
          disabled && "text-muted-foreground/50"
        )}
      >
        {icon}
      </div>

      {!isCollapsed && label.length > 0 && (
        <div
          className={cn(
            "flex-1 ml-2 truncate text-sm",
            subtle && "text-muted-foreground",
            disabled && "text-muted-foreground/50",
            "text-left"
          )}
        >
          {label}
        </div>
      )}

      {!isCollapsed && endElement && !children && <div>{endElement}</div>}
      {!isCollapsed && (children || hasChildren) && (
        <div className="ml-auto text-muted-foreground">
          {isExpanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </div>
      )}
      {!isCollapsed && isExternal && (
        <ExternalLink className="ml-auto h-3 w-3 text-muted-foreground" />
      )}
    </div>
  );

  if (isCollapsed && label) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{item}</TooltipTrigger>
          <TooltipContent side="right">
            <p>{label}</p>
            {isExternal && (
              <p className="text-xs text-muted-foreground">External link</p>
            )}
            {disabled && (
              <p className="text-xs text-muted-foreground">Unavailable</p>
            )}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return item;
};
